import type { TransportRequest } from '../types';
import { resolveHeaders, serialiseAttachment } from '../utils';

/**
 * The default JSON body. Every transport starts from this shape; use
 * `config.body` to add fields and `config.transformRequest` to reshape it.
 *
 * {
 *   message: "latest user text",
 *   conversationId: "c_...",
 *   messages: [{ role, content, attachments? }, ...],
 *   attachments: [{ id, name, size, mimeType, url?, remoteId?, data? }],
 *   stream: true
 * }
 */
export function buildPayload(req: TransportRequest, stream: boolean): Record<string, unknown> {
  const { config } = req;
  const limit = config.historyLimit;
  const history = limit > 0 ? req.messages.slice(-1 * (limit + 1), -1) : [];

  const payload: Record<string, unknown> = {
    message: req.message.content,
    conversationId: req.conversationId,
    messages: history
      .filter((m) => m.status !== 'error')
      .map((m) => ({
        role: m.role,
        content: m.content,
        ...(m.attachments?.length
          ? { attachments: m.attachments.map(serialiseAttachment) }
          : {}),
      })),
    attachments: req.attachments.map(serialiseAttachment),
    stream,
    ...config.body,
  };

  return config.transformRequest ? config.transformRequest(payload, req) : payload;
}

/** True when files themselves still have to travel with the chat request. */
function needsMultipart(req: TransportRequest): boolean {
  return (
    req.config.attachmentMode === 'multipart' &&
    req.attachments.some((a) => a.file instanceof File)
  );
}

/** Issues the chat request, choosing JSON or multipart automatically. */
export async function postChat(req: TransportRequest, stream: boolean): Promise<Response> {
  const { config } = req;
  const payload = buildPayload(req, stream);
  const accept = stream ? 'text/event-stream, application/x-ndjson, text/plain' : 'application/json';

  let body: BodyInit;
  let headers: Record<string, string>;

  if (needsMultipart(req)) {
    const form = new FormData();
    form.append(config.payloadField, JSON.stringify(payload));
    for (const attachment of req.attachments) {
      if (attachment.file) form.append(config.fileField, attachment.file, attachment.name);
    }
    body = form;
    // Content-Type is intentionally omitted: the browser adds the multipart boundary.
    headers = await resolveHeaders(config, { Accept: accept });
  } else {
    body = JSON.stringify(payload);
    headers = await resolveHeaders(config, {
      Accept: accept,
      'Content-Type': 'application/json',
    });
  }

  const response = await fetch(config.apiUrl, {
    method: 'POST',
    headers,
    body,
    signal: req.signal,
    credentials: config.credentials,
  });

  if (!response.ok) {
    throw new Error(await describeHttpError(response));
  }
  return response;
}

export async function describeHttpError(response: Response): Promise<string> {
  let detail = '';
  try {
    const text = await response.text();
    if (text) {
      try {
        const parsed = JSON.parse(text) as Record<string, unknown>;
        detail =
          String(parsed.error ?? parsed.message ?? parsed.detail ?? '') || text.slice(0, 200);
      } catch {
        detail = text.slice(0, 200);
      }
    }
  } catch {
    /* body already consumed or unreadable */
  }
  return `Request failed (${response.status}${response.statusText ? ` ${response.statusText}` : ''})${
    detail ? `: ${detail}` : ''
  }`;
}

/** Decodes a response body into lines, tolerating chunk boundaries mid-line. */
export async function* readLines(response: Response): AsyncGenerator<string> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Streaming is not supported in this environment');

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let index: number;
      while ((index = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, index);
        buffer = buffer.slice(index + 1);
        yield line.endsWith('\r') ? line.slice(0, -1) : line;
      }
    }
    buffer += decoder.decode();
    if (buffer) yield buffer.endsWith('\r') ? buffer.slice(0, -1) : buffer;
  } finally {
    reader.releaseLock?.();
  }
}

/**
 * Pulls text out of an unknown JSON shape. Covers the common cases so most
 * backends work without a custom `parseResponse`.
 */
export function extractText(data: unknown): string {
  if (typeof data === 'string') return data;
  if (!data || typeof data !== 'object') return '';

  const obj = data as Record<string, any>;
  const direct =
    obj.text ??
    obj.reply ??
    obj.answer ??
    obj.output ??
    obj.response ??
    obj.content ??
    obj.delta ??
    obj.message;

  if (typeof direct === 'string') return direct;
  if (direct && typeof direct === 'object') {
    const nested = (direct as Record<string, any>).content ?? (direct as Record<string, any>).text;
    if (typeof nested === 'string') return nested;
  }

  const choice = Array.isArray(obj.choices) ? obj.choices[0] : undefined;
  if (choice) {
    const fromChoice = choice.delta?.content ?? choice.message?.content ?? choice.text;
    if (typeof fromChoice === 'string') return fromChoice;
  }

  return '';
}
